'use client';

import UiIcon from '@/components/ui/UiIcon';
import { type FormEvent, useMemo, useRef, useState } from 'react';
import SiteHeader from '@/components/SiteHeader';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { apiClient } from '@/lib/api';
import { useVacancies } from '@/lib/swr';

const LEVELS = ['Стажёр', 'Junior', 'Middle', 'Senior'] as const;
const EMPLOYMENT = ['Стажировка', 'Частичная занятость', 'Полная занятость'] as const;
const FORMATS = ['Офис (Нальчик)', 'Гибрид', 'Удалённо'] as const;
const SKILLS = ['Python', 'SQL', 'PyTorch', 'TensorFlow', 'React', 'Docker', 'Git', 'Linux'] as const;

type FormState = {
  full_name: string;
  email: string;
  desired_role: string;
  level: string;
  employment: string;
  work_format: string[];
  skills: string[];
  telegram: string;
  portfolio: string;
  comment: string;
  consent: boolean;
};

const INITIAL: FormState = {
  full_name: '',
  email: '',
  desired_role: '',
  level: '',
  employment: '',
  work_format: [],
  skills: [],
  telegram: '',
  portfolio: '',
  comment: '',
  consent: false,
};

function toggle(arr: string[], v: string) {
  return arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
}

export default function VacanciesPage() {
  const toast = useToast();
  const formRef = useRef<HTMLElement | null>(null);
  const { vacancies, isLoading: vacLoading } = useVacancies();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [sending, setSending] = useState(false);

  const roleOptions = useMemo(() => [...vacancies.map((v) => v.title), 'Другое'], [vacancies]);

  const inputCls =
    'w-full rounded-xl bg-site-bg/35 border border-site-border px-4 py-3 text-site-text placeholder-site-muted focus:outline-none focus:ring-2 focus:ring-site-accent/40';
  const labelCls = 'block text-sm font-medium text-site-text mb-2';
  const pillBase =
    'inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors cursor-pointer select-none';
  const pillOn = 'bg-site-accent/15 border-site-accent/45 text-site-text';
  const pillOff = 'bg-site-bg/25 border-site-border text-site-text hover:bg-site-surface/35';

  const canSend = Boolean(form.full_name.trim() && form.email.trim() && form.desired_role.trim() && form.consent);

  const jumpToForm = (role: string) => {
    // Комментарий на русском: быстрый CTA — заполняем позицию и мягко скроллим к анкете.
    setForm((s) => ({ ...s, desired_role: role }));
    window.setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSend) {
      toast.push({ type: 'warning', title: 'Почти готово', message: 'Заполните обязательные поля и подтвердите согласие.' });
      return;
    }

    setSending(true);
    try {
      await apiClient.request<{ ok: boolean; id?: number }>('/api/vacancies/apply/', {
        method: 'POST',
        body: JSON.stringify({ ...form, source: 'frontend:/vacancies' }),
      });
      toast.push({ type: 'success', title: 'Заявка отправлена', message: 'Спасибо! Мы свяжемся с вами в ближайшее время.' });
      setForm(INITIAL);
    } catch (err) {
      toast.push({
        type: 'error',
        title: 'Не удалось отправить',
        message: err instanceof Error ? err.message : 'Ошибка сети',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen text-site-text relative overflow-hidden">


      <SiteHeader />

      {/* Герой */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-20 pb-10 text-center">
        <Badge variant="custom" color="#F59E0B" className="mb-6 text-sm">
          <UiIcon name="Briefcase" /> Вакансии
        </Badge>
        <h1 className="text-4xl md:text-6xl font-bold mb-5 text-site-text">
          Станьте частью команды ЛИСАД
        </h1>
        <p className="text-lg md:text-xl text-site-secondary max-w-4xl mx-auto leading-relaxed">
          Мы растём и ищем людей, которым нравится делать сильные продукты и исследования на стыке данных и ИИ.
        </p>
      </section>

      <div className="relative z-10 max-w-6xl mx-auto px-6 pb-20 space-y-8">
        {/* Позиции */}
        <section>
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold">Кого мы ищем</h2>
              <p className="text-site-secondary mt-2">Выберите роль — анкета ниже заполнится быстрее.</p>
            </div>
            <Badge variant="secondary" size="sm">
              <UiIcon name="Lightning" /> Отклик ~ 1–2 минуты
            </Badge>
          </div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-5">
            {vacLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Card key={i} className="p-6 animate-pulse">
                  <div className="h-5 w-2/3 bg-site-raised/40 rounded mb-3" />
                  <div className="h-4 w-11/12 bg-site-raised/30 rounded mb-4" />
                  <div className="flex gap-2">
                    <div className="h-6 w-16 bg-site-raised/30 rounded-full" />
                    <div className="h-6 w-20 bg-site-raised/30 rounded-full" />
                    <div className="h-6 w-14 bg-site-raised/30 rounded-full" />
                  </div>
                </Card>
              ))
            ) : vacancies.length === 0 ? (
              <Card className="p-6 text-site-secondary md:col-span-2">
                Вакансии скоро появятся. Можно отправить заявку через анкету ниже.
                <div className="mt-4">
                  <Button size="sm" onClick={() => jumpToForm('Другое')}>
                    Заполнить анкету
                  </Button>
                </div>
              </Card>
            ) : (
              vacancies.map((v) => (
                <Card key={v.id} variant="glass" useWidgetSettings={true} hover className="overflow-hidden">
                <div className="p-6 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-xl font-bold text-site-text">{v.title}</h3>
                      <p className="text-site-secondary mt-1">{v.subtitle}</p>
                    </div>
                    <Badge variant="success" size="sm">
                      Открыто
                    </Badge>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {v.tags.map((t) => (
                      <Badge key={t} variant="secondary" size="sm">
                        {t}
                      </Badge>
                    ))}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-site-muted mb-2">Что делать</div>
                      <ul className="space-y-1 text-site-text">
                        {v.tasks.map((x) => (
                          <li key={x}>• {x}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="text-site-muted mb-2">Будет плюсом</div>
                      <ul className="space-y-1 text-site-text">
                        {v.plus.map((x) => (
                          <li key={x}>• {x}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="pt-1">
                    <Button size="sm" onClick={() => jumpToForm(v.title)}>
                      Откликнуться
                    </Button>
                  </div>
                </div>
              </Card>
              ))
            )}
          </div>
        </section>

        {/* Анкета */}
        <section ref={formRef}>
          <Card variant="glass" useWidgetSettings={true} className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold">Анкета кандидата</h2>
                <p className="text-site-secondary mt-2">Почти всё — через списки и галочки, чтобы было быстро и приятно.</p>
              </div>
              <Badge variant="secondary" size="sm">
                <UiIcon name="Lock" /> Данные видит только команда
              </Badge>
            </div>

            <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Имя и фамилия *</label>
                <input
                  className={inputCls}
                  value={form.full_name}
                  onChange={(e) => setForm((s) => ({ ...s, full_name: e.target.value }))}
                  placeholder="Например: Иван Иванов"
                  autoComplete="name"
                  required
                />
              </div>

              <div>
                <label className={labelCls}>Email *</label>
                <input
                  className={inputCls}
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                  placeholder="name@example.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label className={labelCls}>Позиция *</label>
                <select
                  className={inputCls}
                  value={form.desired_role}
                  onChange={(e) => setForm((s) => ({ ...s, desired_role: e.target.value }))}
                  required
                >
                  <option value="">Выберите…</option>
                  {roleOptions.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Уровень</label>
                <select className={inputCls} value={form.level} onChange={(e) => setForm((s) => ({ ...s, level: e.target.value }))}>
                  <option value="">Выберите…</option>
                  {LEVELS.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Занятость</label>
                <select
                  className={inputCls}
                  value={form.employment}
                  onChange={(e) => setForm((s) => ({ ...s, employment: e.target.value }))}
                >
                  <option value="">Выберите…</option>
                  {EMPLOYMENT.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Telegram</label>
                <input
                  className={inputCls}
                  value={form.telegram}
                  onChange={(e) => setForm((s) => ({ ...s, telegram: e.target.value }))}
                  placeholder="@username (если удобно)"
                  autoComplete="off"
                />
              </div>

              <div className="md:col-span-2">
                <div className={labelCls}>Формат работы</div>
                <div className="flex flex-wrap gap-2">
                  {FORMATS.map((x) => (
                    <label key={x} className={`${pillBase} ${form.work_format.includes(x) ? pillOn : pillOff}`}>
                      <input
                        type="checkbox"
                        className="accent-blue-500"
                        checked={form.work_format.includes(x)}
                        onChange={() => setForm((s) => ({ ...s, work_format: toggle(s.work_format, x) }))}
                      />
                      {x}
                    </label>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <div className={labelCls}>Навыки (что уже уверенно)</div>
                <div className="flex flex-wrap gap-2">
                  {SKILLS.map((x) => (
                    <label key={x} className={`${pillBase} ${form.skills.includes(x) ? pillOn : pillOff}`}>
                      <input
                        type="checkbox"
                        className="accent-blue-500"
                        checked={form.skills.includes(x)}
                        onChange={() => setForm((s) => ({ ...s, skills: toggle(s.skills, x) }))}
                      />
                      {x}
                    </label>
                  ))}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className={labelCls}>Ссылка на портфолио / GitHub</label>
                <input
                  className={inputCls}
                  value={form.portfolio}
                  onChange={(e) => setForm((s) => ({ ...s, portfolio: e.target.value }))}
                  placeholder="https://github.com/..."
                  autoComplete="url"
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelCls}>Комментарий (по желанию)</label>
                <textarea
                  className={`${inputCls} min-h-[110px]`}
                  value={form.comment}
                  onChange={(e) => setForm((s) => ({ ...s, comment: e.target.value }))}
                  placeholder="Коротко: чем хотите заниматься и почему ЛИСАД?"
                />
              </div>

              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
                <label className="flex items-center gap-2 text-sm text-site-text cursor-pointer select-none">
                  <input
                    type="checkbox"
                    className="accent-blue-500"
                    checked={form.consent}
                    onChange={(e) => setForm((s) => ({ ...s, consent: e.target.checked }))}
                  />
                  Согласен(на) на обработку персональных данных *
                </label>

                <Button type="submit" loading={sending} disabled={!canSend}>
                  Отправить заявку
                </Button>
              </div>
            </form>
          </Card>
        </section>
      </div>
    </div>
  );
}


